import { useEffect, useState } from "react";
import { Lock } from "lucide-react";
import { toast } from "sonner";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api } from "../../../services/api";
import { TokenManager } from "../../../services/apiClient";
import {
  AuthShell,
  AuthBanner,
  AuthInput,
  AuthButton,
  PasswordRuleChecklist,
  allRulesMet,
  PWD_RULES,
} from "./_primitives";

export function CompleteSignUpPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      setError(t("completeSignup.errors.invalidLink"));
      return;
    }
    // Strip the activation token from the URL — keeps it out of browser
    // history, screen-share previews, and cross-origin referrer headers.
    if (typeof window !== "undefined") {
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [token, t]);

  const allMet = allRulesMet(password);
  const passwordsMatch = password.length > 0 && password === confirmPassword;
  const canSubmit = Boolean(token) && allMet && passwordsMatch && !isLoading;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Guard against rapid double-submit via Enter-key spam.
    if (isLoading) return;
    setError("");
    if (!token) return;
    if (!allMet) { setError(t("completeSignup.errors.rulesNotMet")); return; }
    if (!passwordsMatch) { setError(t("completeSignup.errors.mismatch")); return; }

    setIsLoading(true);
    try {
      const response = await api.completeSignup({ token, password });
      if (response && response.token) {
        TokenManager.setAccessToken(response.token);
        await queryClient.invalidateQueries({ queryKey: ["currentUser"] });
        toast.success(t("completeSignup.toasts.activated"), { description: t("completeSignup.toasts.activatedDescription") });
        navigate("/welcome");
      } else {
        throw new Error("Invalid response format");
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "";
      const code = (err as { code?: string } | undefined)?.code || "";
      const lowered = (code + " " + message).toLowerCase();
      if (
        code === "INVALID_TOKEN" || code === "EXPIRED_TOKEN" ||
        lowered.includes("invalid") || lowered.includes("expired")
      ) {
        setError(t("completeSignup.errors.expired"));
      } else if (code === "ALREADY_EXISTS" || lowered.includes("already")) {
        setError(t("completeSignup.errors.alreadyActive"));
      } else {
        setError(t("completeSignup.errors.generic"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthShell
      pageTitle={t("completeSignup.pageTitle")}
      pageDescription={t("completeSignup.pageDescription")}
    >
      <div className="space-y-8">
        <header className="text-center">
          <h1 className="editorial-display">{t("completeSignup.title")}</h1>
          <p className="editorial-subtitle">
            {t("completeSignup.subtitle")}
          </p>
        </header>

        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          {error && <AuthBanner variant="error">{error}</AuthBanner>}

          <div>
            <AuthInput
              label={t("completeSignup.passwordLabel")}
              type="password"
              name="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t("completeSignup.passwordPlaceholder")}
              autoComplete="new-password"
              autoCapitalize="off"
              spellCheck={false}
              disabled={isLoading || !token}
              leadingIcon={<Lock className="w-4 h-4" aria-hidden="true" />}
              withPasswordToggle
              aria-invalid={password.length > 0 && !allMet ? "true" : undefined}
              aria-describedby="completesignup-pwd-rules"
            />
            <PasswordRuleChecklist id="completesignup-pwd-rules" password={password} rules={PWD_RULES} />
          </div>

          <AuthInput
            label={t("completeSignup.confirmLabel")}
            type="password"
            name="confirmPassword"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder={t("completeSignup.confirmPlaceholder")}
            autoComplete="new-password"
            autoCapitalize="off"
            spellCheck={false}
            disabled={isLoading || !token}
            leadingIcon={<Lock className="w-4 h-4" aria-hidden="true" />}
            aria-invalid={confirmPassword.length > 0 && !passwordsMatch ? "true" : undefined}
            error={confirmPassword.length > 0 && !passwordsMatch ? t("password.match") : undefined}
          />

          <AuthButton
            type="submit"
            variant="primary"
            fullWidth
            loading={isLoading}
            loadingLabel={t("completeSignup.submitLoading")}
            disabled={!canSubmit}
          >
            {t("completeSignup.submit")}
          </AuthButton>

          {!token && (
            <p className="text-sm text-[var(--muted-foreground)] text-center pt-2">
              {t("completeSignup.needNewLinkPrefix")}{" "}
              <Link to="/signup" className="underline underline-offset-2 text-[var(--foreground)] hover:text-[var(--muted-foreground)] transition-colors motion-reduce:transition-none font-medium">
                {t("completeSignup.needNewLinkAction")}
              </Link>
            </p>
          )}
        </form>
      </div>
    </AuthShell>
  );
}
